// ============================================
// Rebate CSV Transform Workflow — Shared Types
// ============================================
// These types are the contract between the frontend manual workflow
// and the backend auto-transform handler. Both sides read/write
// snapshot `state` using these shapes.

// --- Step Labels ---

export const REBATE_CSV_TRANSFORM_STEPS = {
  EXTRACT_CONSTANTS: "extract_constants",
  PREPROCESS: "preprocess",
  MAP_FIELDS: "map_fields",
  TRANSFORM: "transform",
} as const;

export type RebateCsvTransformStepLabel =
  (typeof REBATE_CSV_TRANSFORM_STEPS)[keyof typeof REBATE_CSV_TRANSFORM_STEPS];

/**
 * Canonical execution order of the transform steps. Used to determine which
 * steps are "downstream" of a given step so their snapshots can be deleted
 * when an upstream step is re-saved (e.g. editing map_fields makes any existing
 * transform snapshot stale).
 */
export const REBATE_CSV_TRANSFORM_STEP_ORDER: readonly RebateCsvTransformStepLabel[] =
  [
    REBATE_CSV_TRANSFORM_STEPS.EXTRACT_CONSTANTS,
    REBATE_CSV_TRANSFORM_STEPS.PREPROCESS,
    REBATE_CSV_TRANSFORM_STEPS.MAP_FIELDS,
    REBATE_CSV_TRANSFORM_STEPS.TRANSFORM,
  ];

/** Step labels that run strictly after the given step, in execution order. */
export function rebateCsvStepsAfter(
  stepLabel: string,
): RebateCsvTransformStepLabel[] {
  const idx = REBATE_CSV_TRANSFORM_STEP_ORDER.indexOf(
    stepLabel as RebateCsvTransformStepLabel,
  );
  if (idx === -1) return [];
  return [...REBATE_CSV_TRANSFORM_STEP_ORDER.slice(idx + 1)];
}

// --- Workflow Constants (supplier, currency, dates) ---

export interface WorkflowConstantsDateRange {
  startDate: string;
  endDate: string;
}

export interface WorkflowConstants {
  supplierId: string;
  currency: string;
  conversionRate?: number;
  targetCurrency?: string;
  dateRange?: WorkflowConstantsDateRange;
}

// --- Field Mappings ---

export type MappingMatchSource = "direct" | "alias" | "ai" | "user";

export type MappingValue = SimpleMappingValue | SplitMappingValue;

export interface SimpleMappingValue {
  type: "simple";
  value: string;
  matchSource?: MappingMatchSource;
}

export interface SplitMappingValue {
  type: "split";
  splits: Array<{
    value: string;
    percentage: number;
  }>;
  matchSource?: MappingMatchSource;
}

export interface CompositeMapping {
  sourceColumnNames: string[];
  targetColumnName: string;
  mappings: Record<string, string | MappingValue>;
  separator?: string;
}

export interface FieldMappings {
  divisionMappings: CompositeMapping;
  productMappings: CompositeMapping;
  dateMappings?: CompositeMapping;
  supplierMappings?: CompositeMapping;
}

// --- Per-step Snapshot State ---

export interface CsvTransformWorkflowStep {
  type: string;
  description: string;
}

export interface PreprocessSnapshotState {
  workflowSteps?: CsvTransformWorkflowStep[];
  instructions?: string;
  fileNames?: string[];
}

export type RebateSenderMatchOutcome = "unique" | "several" | "none";

export interface RebateSenderMatchSnapshot {
  outcome: RebateSenderMatchOutcome;
  senderEmail: string;
}

export interface ExtractConstantsSnapshotState {
  constants: WorkflowConstants;
  senderMatch?: RebateSenderMatchSnapshot;
}

export interface MapFieldsSnapshotState {
  constants: WorkflowConstants;
  mappings: FieldMappings;
  explanations?: string[];
}

export interface TransformSnapshotState {
  workflowSteps?: CsvTransformWorkflowStep[];
  explanations?: string[];
  constants: WorkflowConstants;
}

/**
 * Discriminated snapshot state map keyed by step label.
 * Usage: `RebateCsvSnapshotStateMap[typeof REBATE_CSV_TRANSFORM_STEPS.PREPROCESS]`
 */
export interface RebateCsvSnapshotStateMap {
  preprocess: PreprocessSnapshotState;
  extract_constants: ExtractConstantsSnapshotState;
  map_fields: MapFieldsSnapshotState;
  transform: TransformSnapshotState;
}
